import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import UIIcon from '@/components/UI/UIIcon'
import { useGSAP } from '@/composables/useGSAP'
import { useImageFormatContext } from '@/composables/useImageFormatContext'
import { getContentfulUrl } from '@/utils/contentful'

type RunnerGoalIconProps = {
  searched: boolean
  collected: boolean
  failed?: boolean
  image: string
  index: number
  ordered?: boolean
  className?: string
}

export default function RunnerGoalIcon({
  searched,
  collected,
  failed = false,
  image,
  index,
  ordered = true,
  className = '',
}: RunnerGoalIconProps) {
  const { gsap } = useGSAP()

  const arrowRef = useRef<SVGSVGElement>(null)
  const arrowBodyRef = useRef<SVGPathElement>(null)
  const arrowHeadRef = useRef<SVGPathElement>(null)
  const arrowBodyAltRef = useRef<SVGPathElement>(null)
  const arrowHeadAltRef = useRef<SVGPathElement>(null)

  const [previousSearched, setPreviousSearched] = useState<boolean>(false)

  const betterFormat = useImageFormatContext()
  const [imageUrl, setImageUrl] = useState(null)
  useEffect(() => {
    const url = getContentfulUrl(`/src/assets/runner/${image}.png`, {
      w: window.innerWidth < 768 ? 160 : 188,
      fm: betterFormat,
    })
    setImageUrl(url)
  }, [image, betterFormat])

  const backgroundColorClassName = (() => {
    if (searched) return 'text-mid-blue'
    if (failed) return 'text-coral'
    if (collected) return 'text-mid-blue'
    return 'text-secondary'
  })()

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      if (searched) {
        gsap
          .timeline({ delay: 0.5 })
          .to(arrowRef.current, { opacity: 1, duration: 0.5, ease: 'power2.out' }, 0)
          .fromTo(
            [arrowBodyAltRef.current, arrowBodyRef.current],
            { drawSVG: '0% 0%' },
            { drawSVG: '0% 100%', duration: 1, ease: 'power2.out', stagger: 0.15 },
            0
          )
          .fromTo(
            [arrowHeadAltRef.current, arrowHeadRef.current],
            { drawSVG: '50% 50%' },
            { drawSVG: '0% 100%', duration: 0.3, ease: 'power2.out', stagger: 0.15 },
            0.7
          )
      } else {
        gsap
          .timeline()
          .to(arrowRef.current, { opacity: 0, duration: previousSearched ? 0.5 : 0, ease: 'power2.out' }, 0)
      }
      setPreviousSearched(searched)
    })

    return () => ctx.kill()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searched, arrowBodyRef, arrowHeadRef, gsap, setPreviousSearched])

  return (
    <div className={`Runner_goal-icon w-80 h-80 xl:w-96 xl:h-96 relative ${className}`}>
      {index >= 0 && (
        <UIIcon
          icon='background3'
          className={`absolute w-64 h-64 xl:w-80 xl:h-80 top-8 left-8 transition-transform duration-700 ease-out-back ${searched ? 'rotate-90' : ''} ${backgroundColorClassName}`}
        />
      )}
      <div
        className={`relative block w-full h-full transition-opacity duration-500 ease-out-quad ${collected || failed ? 'opacity-0' : ''}`}
      >
        <img
          src={imageUrl}
          alt={image}
          className={`relative block w-full h-full object-contain transition-transform duration-700 ease-out-back ${ordered ? (searched ? 'scale-[100%] lg:scale-[110%] xl:scale-[120%]' : 'scale-[60%]') : ''}`}
        />
      </div>
      <div
        className={`absolute top-0 left-0 w-full h-full flex items-center justify-center transition-opacity duration-500 ease-out-quad ${collected && !failed ? '' : 'opacity-0'}`}
      >
        <UIIcon
          icon='checkedRunner'
          className={`relative text-48 md:text-64 text-cream transition-transform duration-700 ease-out-back ${collected && !failed ? '' : '-rotate-45 scale-0'}`}
        />
      </div>
      <div
        className={`absolute top-0 left-0 w-full h-full flex items-center justify-center transition-opacity duration-500 ease-out-quad ${failed ? '' : 'opacity-0'}`}
      >
        <UIIcon
          icon='failed'
          className={`relative text-48 md:text-64 text-mid-blue transition-transform duration-700 ease-out-back ${failed ? '' : '-rotate-45 scale-0'}`}
        />
      </div>

      <svg
        ref={arrowRef}
        className='absolute top-[75%] md:top-[85%] -left-16 w-64 h-64 md:w-80 md:h-80 text-primary'
        viewBox='0 0 80 80'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          ref={arrowBodyAltRef}
          d='M20.1399 66.7258C66.9892 45.7391 41.195 19.7617 29.9175 28.0308C15.8207 38.3672 48.499 62.4067 64.5679 19.2146'
          stroke='currentColor'
          strokeWidth='2'
          className='opacity-30'
        />
        <path
          ref={arrowHeadAltRef}
          d='M66.5902 28.5113L64.8876 18.6602L55.43 22.7858'
          stroke='currentColor'
          strokeWidth='2'
          className='opacity-30'
        />
        <path
          ref={arrowBodyRef}
          d='M20.1399 66.7258C66.9892 45.7391 41.195 19.7617 29.9175 28.0308C15.8207 38.3672 48.499 62.4067 64.5679 19.2146'
          stroke='currentColor'
          strokeWidth='2'
        />
        <path
          ref={arrowHeadRef}
          d='M66.5902 28.5113L64.8876 18.6602L55.43 22.7858'
          stroke='currentColor'
          strokeWidth='2'
        />
      </svg>
    </div>
  )
}
